import { Subject } from 'rxjs';

import { DrawableObject, RenderMode } from '../renderer';
import { ViewFrustum, FrumstumContainment, BoundingBox, BoundingBoxObject, Octree } from '../util';
import { Ray } from '../math';

/**
 * Thin wrapper that lets a DrawableObject live inside an Octree.
 * The octree requires BoundingBoxObject; this bridges the gap.
 */
class OctreeEntry implements BoundingBoxObject {
  constructor(public readonly obj: DrawableObject) {}

  getBoundingBox(): BoundingBox {
    return this.obj.bounds!.boundingBox;
  }
  canIntersectRay(): boolean {
    return false;
  }
  intersectsRay(_ray: Ray): boolean {
    return false;
  }
}

export class ObjectManager {
  public added: Subject<DrawableObject>;
  public removed: Subject<DrawableObject>;
  private objects: DrawableObject[];
  // Mirrors each object's index in `objects` so removeObject is O(1)
  // (swap-pop). Tile-streamed scatter regions evict hundreds of objects per
  // tile crossing — the old indexOf+splice path was O(N²) with N≈10k.
  private objectIndex = new Map<DrawableObject, number>();
  // Subset of `objects` containing only entries with culling !== 'octree'.
  // The linear walk in getAllVisibleObjects iterates this list instead of
  // every registered object — at 100K scatter (all octree-mode), the master
  // walk would dominate cull CPU even after the inner-loop reorder. This
  // list typically holds ~50 entries (player, projectiles, authored static).
  // Maintained alongside `objects` via swap-pop on remove + sort in
  // ensureSorted; keeps both arrays consistent without a per-frame rebuild.
  private nonOctreeObjects: DrawableObject[] = [];
  private nonOctreeIndex = new Map<DrawableObject, number>();
  // Set by add/remove; consumed lazily by ensureSorted() at iteration sites.
  // Per-add sortByOrder() was O(N log N) and dwarfed the actual add cost during
  // streamer bursts. order is set once on RenderObject construction (1000 for
  // opaque, 2000 for translucent) and never mutated post-add, so a single
  // sort-on-read is correct.
  private sortDirty = false;

  private octree: Octree<OctreeEntry> | null = null;
  private octreeEntries = new Map<DrawableObject, OctreeEntry>();
  // Octree.prune() removes empty branches. Without periodic prunes, scatter
  // tile eviction leaves the tree fully split with empty leaves, growing
  // both memory and getObjectsInFrustum's per-call branch-walk cost over
  // time. Amortize by pruning every PRUNE_INTERVAL removes.
  private octreeRemovesSincePrune = 0;
  private static readonly PRUNE_INTERVAL = 256;

  // Lazily-built list of non-static depth casters used by LightManager
  // every shadow frame. Walking `objects` to filter by `static !== true`
  // and modes.includes(Depth) costs ~1ms per frame at 19K, ~5ms at 100K —
  // this cache amortizes the work to add/remove time. Invalidated whenever
  // the membership might change.
  private nonStaticDepthCastersCache: DrawableObject[] | null = null;

  // Per-frame visibility cache: computed once per frustum, reused across modes.
  // Per-mode result arrays so callers can hold references simultaneously.
  private cachedFrustum: ViewFrustum | null = null;
  private cachedOctreeVisible: DrawableObject[] = [];
  // Per-entry RenderMode bitmask, parallel to `cachedOctreeVisible`. Lets the
  // mode-filter loop replace `modes.includes(mode)` (linear array search)
  // with `(mask & (1 << mode)) !== 0` (single integer AND). Safe because
  // octree-mode objects (scatter, far billboards) don't mutate their `modes`
  // array after registration — only list/none-mode overlays do that.
  private cachedOctreeMasks: number[] = [];
  private visibleResults = new Map<RenderMode, DrawableObject[]>();

  // Optional debug filter applied to every object query (visible + by-mode).
  // Returning false hides the object from rendering (gbuffer, shadow, etc.)
  // without removing it from the scene. Used by debug toggles like
  // 'hide-animated' to do clean A/B perf tests.
  public renderFilter: ((obj: DrawableObject) => boolean) | null = null;

  constructor() {
    this.objects = [];

    this.added = new Subject();
    this.removed = new Subject();
  }

  /**
   * Initialize octree spatial index for objects with culling: "octree".
   * Must be called before adding octree-culled objects (typically during scene activation).
   *
   * `minimumSize` is the smallest leaf half-extent the tree will subdivide
   * to. Default 10m suits indoor/Sponza-scale worlds. For km-scale outdoor
   * scatter scenes, larger leaves dramatically reduce internal-node count
   * (cuts the per-frame tree-walk cost) and reduce object straddling at
   * leaf boundaries (each tree usually fits inside one leaf instead of
   * crossing 2-4). Pick ~world_size / 200 as a starting point.
   */
  public initOctree(bounds: BoundingBox, minimumSize: number = 10.0): void {
    this.octree = Octree.create<OctreeEntry>(bounds, minimumSize);
  }

  /**
   * Invalidate the per-frame visibility cache. Call at the start of each
   * frame before the first getAllVisibleObjects call.
   */
  public invalidateVisibilityCache(): void {
    this.cachedFrustum = null;
  }

  // Cull-breakdown stats populated by getAllVisibleObjects. Reset by
  // resetCullStats() at the start of each frame; accumulated across the
  // multiple per-mode calls. DeferredSceneRenderer reads them out for the
  // perf overlay so we can see where cullingMs goes (linear walk vs octree
  // query vs cache filter).
  public cullStats = {
    listWalkMs: 0,
    octreeQueryMs: 0,
    octreeCacheFilterMs: 0,
    listResultCount: 0,
    octreeReturnedCount: 0,
    octreeCacheHits: 0,
    totalObjectCount: 0,
  };

  public resetCullStats(): void {
    const s = this.cullStats;
    s.listWalkMs = 0;
    s.octreeQueryMs = 0;
    s.octreeCacheFilterMs = 0;
    s.listResultCount = 0;
    s.octreeReturnedCount = 0;
    s.octreeCacheHits = 0;
    s.totalObjectCount = this.objects.length;
  }

  /**
   * Returns all visible objects for the given mode, combining both
   * list-culled and octree-culled results.
   *
   * The octree query is cached per frustum instance — multiple calls with the
   * same frustum (e.g. GBuffer, Primary, Transparent, Overlay) reuse the
   * octree result and only filter by mode.
   */
  public getAllVisibleObjects(mode: RenderMode, frustum: ViewFrustum): DrawableObject[] {
    this.ensureSorted();
    let result = this.visibleResults.get(mode);
    if (!result) {
      result = [];
      this.visibleResults.set(mode, result);
    }
    result.length = 0;

    const filter = this.renderFilter;
    const stats = this.cullStats;

    // Walk only the non-octree subset (~50 entries typically). Scatter trees
    // with culling: 'octree' live in the octree-only path below; including
    // them here would re-add the linear-with-N cost we just paid to remove.
    const listStart = performance.now();
    const initialResultLen = result.length;
    for (const obj of this.nonOctreeObjects) {
      const cfg = obj.renderConfiguration;
      if (!cfg.modes?.includes(mode)) continue;
      if (filter && !filter(obj)) continue;
      if (cfg.culling === 'list') {
        if (
          frustum.containsBoundingSphere(obj.bounds!.boundingSphere) !==
          FrumstumContainment.None
        ) {
          result.push(obj);
        }
      } else {
        // 'none' or undefined — no culling, always include
        result.push(obj);
      }
    }
    stats.listWalkMs += performance.now() - listStart;
    stats.listResultCount += result.length - initialResultLen;

    if (!this.octree) return result;

    // Cache octree query per frustum — same frustum across modes avoids re-traversal.
    if (this.cachedFrustum !== frustum) {
      const queryStart = performance.now();
      this.cachedFrustum = frustum;
      const entries = this.octree.getObjectsInFrustum(frustum);
      const cache = this.cachedOctreeVisible;
      const masks = this.cachedOctreeMasks;
      cache.length = 0;
      masks.length = 0;
      for (let i = 0; i < entries.length; i++) {
        const obj = entries[i].obj;
        cache.push(obj);
        // Pack the obj's RenderMode list into a bitmask once per cache rebuild
        // so the mode-filter loop below is a single AND instead of array
        // includes(). 8K visible × 2 mode passes was ~1ms via includes.
        let mask = 0;
        const modes = obj.renderConfiguration.modes;
        if (modes) {
          for (let j = 0; j < modes.length; j++) mask |= 1 << modes[j];
        }
        masks.push(mask);
      }
      stats.octreeQueryMs += performance.now() - queryStart;
      stats.octreeReturnedCount = cache.length;
    } else {
      stats.octreeCacheHits += 1;
    }

    // Filter cached octree results by mode via the precomputed bitmask.
    const filterStart = performance.now();
    const cache = this.cachedOctreeVisible;
    const masks = this.cachedOctreeMasks;
    const modeBit = 1 << mode;
    for (let i = 0; i < cache.length; i++) {
      if ((masks[i] & modeBit) === 0) continue;
      const obj = cache[i];
      if (filter && !filter(obj)) continue;
      result.push(obj);
    }
    stats.octreeCacheFilterMs += performance.now() - filterStart;

    return result;
  }

  public getObjects(mode: RenderMode): DrawableObject[] {
    this.ensureSorted();
    const filter = this.renderFilter;
    return this.objects.filter(
      (obj) =>
        obj.renderConfiguration.modes?.includes(mode) && (filter === null || filter(obj)),
    );
  }

  /**
   * Returns the cached list of objects with `RenderMode.Depth` set and
   * `renderConfiguration.static !== true`. Used by LightManager every shadow
   * frame to find dynamic shadow casters; caching lets the cost scale with
   * mutation rate (≈0) instead of total object count.
   *
   * Invalidated by add / remove / clear / direct mutation invalidator.
   * Note: callers that flip an existing object's `static` or `modes` after
   * registration must call `invalidateNonStaticCache()` themselves —
   * ObjectManager has no notification hook for those mutations. The current
   * scatter sites set both flags BEFORE addObject, so the default flow is
   * already consistent.
   */
  public getNonStaticDepthCasters(): DrawableObject[] {
    if (this.nonStaticDepthCastersCache) return this.nonStaticDepthCastersCache;
    const result: DrawableObject[] = [];
    for (const obj of this.objects) {
      const cfg = obj.renderConfiguration;
      if (cfg.static === true) continue;
      if (!cfg.modes?.includes(RenderMode.Depth)) continue;
      result.push(obj);
    }
    this.nonStaticDepthCastersCache = result;
    return result;
  }

  /**
   * Drop the non-static depth casters cache. Call after directly mutating an
   * already-registered object's `renderConfiguration.static` or `.modes`
   * fields (the cache rebuilds lazily on next read).
   */
  public invalidateNonStaticCache(): void {
    this.nonStaticDepthCastersCache = null;
  }

  /** Returns every registered object regardless of render mode. */
  public getAllObjects(): DrawableObject[] {
    this.ensureSorted();
    return this.objects;
  }

  public addObject(obj: DrawableObject) {
    if (this.objectIndex.has(obj)) return;

    this.applyDefaultRenderConfig(obj);
    this.registerForCulling(obj);

    this.objectIndex.set(obj, this.objects.length);
    this.objects.push(obj);
    if (obj.renderConfiguration.culling !== 'octree') {
      this.nonOctreeIndex.set(obj, this.nonOctreeObjects.length);
      this.nonOctreeObjects.push(obj);
    }
    this.sortDirty = true;
    this.nonStaticDepthCastersCache = null;

    this.added.next(obj);
  }

  /**
   * Bulk-add many objects in a single pass. For octree-culled objects this
   * defers Octree.distribute() until every entry has been queued, so the
   * split/redistribute cascade runs once instead of once per insert. Critical
   * for scatter loads where adding 10K+ trees one-by-one through addObject
   * turns a sub-second load into seconds.
   */
  public addObjects(objs: DrawableObject[]): void {
    const pendingOctreeEntries: OctreeEntry[] = [];
    let addedAny = false;

    for (const obj of objs) {
      if (this.objectIndex.has(obj)) continue;

      this.applyDefaultRenderConfig(obj);

      // Inline registerForCulling, but for octree-mode objects collect entries
      // and defer the Octree.insertArray call so distribute() runs once below.
      switch (obj.renderConfiguration.culling) {
        case 'octree': {
          if (!this.octree) {
            throw new Error(
              'Octree not initialized. Call initOctree(bounds) before adding octree-culled objects.',
            );
          }
          if (!obj.bounds) {
            throw new Error('Object must have bounds to enable octree culling');
          }
          const entry = new OctreeEntry(obj);
          this.octreeEntries.set(obj, entry);
          pendingOctreeEntries.push(entry);
          break;
        }
        case 'list':
          if (!obj.bounds) {
            throw new Error('Object must have bounds to enable culling');
          }
          break;
        case 'none':
        default:
          break;
      }

      this.objectIndex.set(obj, this.objects.length);
      this.objects.push(obj);
      if (obj.renderConfiguration.culling !== 'octree') {
        this.nonOctreeIndex.set(obj, this.nonOctreeObjects.length);
        this.nonOctreeObjects.push(obj);
      }
      this.added.next(obj);
      addedAny = true;
    }

    if (pendingOctreeEntries.length > 0) {
      this.octree!.insertArray(pendingOctreeEntries);
    }
    if (addedAny) {
      this.sortDirty = true;
      this.nonStaticDepthCastersCache = null;
    }
  }

  private applyDefaultRenderConfig(obj: DrawableObject): void {
    if (!obj.renderConfiguration.modes) {
      obj.renderConfiguration.modes = [
        RenderMode.Primary,
        RenderMode.Picking,
        RenderMode.Occlusion,
        RenderMode.Depth,
      ];
    }
    if (!obj.renderConfiguration.culling) {
      obj.renderConfiguration.culling = 'list';
    }
  }

  private registerForCulling(obj: DrawableObject): void {
    switch (obj.renderConfiguration.culling) {
      case 'octree': {
        if (!this.octree) {
          throw new Error(
            'Octree not initialized. Call initOctree(bounds) before adding octree-culled objects.',
          );
        }
        if (!obj.bounds) {
          throw new Error('Object must have bounds to enable octree culling');
        }
        const entry = new OctreeEntry(obj);
        this.octreeEntries.set(obj, entry);
        this.octree.insert(entry);
        return;
      }
      case 'list':
        if (!obj.bounds) {
          throw new Error('Object must have bounds to enable culling');
        }
        return;
      case 'none':
      default:
        return;
    }
  }

  private ensureSorted(): void {
    if (!this.sortDirty) return;
    const cmp = (a: DrawableObject, b: DrawableObject) =>
      (a.renderConfiguration.order ?? 0) - (b.renderConfiguration.order ?? 0);
    this.objects.sort(cmp);
    for (let i = 0; i < this.objects.length; i++) {
      this.objectIndex.set(this.objects[i], i);
    }
    this.nonOctreeObjects.sort(cmp);
    for (let i = 0; i < this.nonOctreeObjects.length; i++) {
      this.nonOctreeIndex.set(this.nonOctreeObjects[i], i);
    }
    this.sortDirty = false;
  }

  public clear(): void {
    // Give every object a chance to release GL resources before we drop our
    // references. Without this, meshes that own textures/VAOs (e.g. animation
    // data textures, debug visualizers) leak across scene transitions.
    for (const obj of this.objects) {
      try {
        obj.destroy?.();
      } catch (e) {
        console.error('ObjectManager.clear: object destroy() threw', e);
      }
    }

    this.objects = [];
    this.objectIndex.clear();
    this.nonOctreeObjects = [];
    this.nonOctreeIndex.clear();
    this.sortDirty = false;
    this.nonStaticDepthCastersCache = null;
    this.octreeEntries.clear();
    this.octreeRemovesSincePrune = 0;
    if (this.octree) {
      this.octree = Octree.create<OctreeEntry>(this.octree.region);
    }
  }

  public removeObject(obj: DrawableObject) {
    const index = this.objectIndex.get(obj);
    if (index === undefined) return;

    const last = this.objects.length - 1;
    if (index !== last) {
      const moved = this.objects[last];
      this.objects[index] = moved;
      this.objectIndex.set(moved, index);
      // Swap-pop breaks order; defer the resort to the next read.
      this.sortDirty = true;
    }
    this.objects.pop();
    this.objectIndex.delete(obj);
    this.nonStaticDepthCastersCache = null;

    // Mirror the swap-pop in nonOctreeObjects when this entry was tracked
    // there. ensureSorted re-syncs both index maps after a sort, so the
    // sortDirty flag set above also covers nonOctreeIndex consistency.
    const nonOctreeIdx = this.nonOctreeIndex.get(obj);
    if (nonOctreeIdx !== undefined) {
      const nonOctreeLast = this.nonOctreeObjects.length - 1;
      if (nonOctreeIdx !== nonOctreeLast) {
        const moved = this.nonOctreeObjects[nonOctreeLast];
        this.nonOctreeObjects[nonOctreeIdx] = moved;
        this.nonOctreeIndex.set(moved, nonOctreeIdx);
      }
      this.nonOctreeObjects.pop();
      this.nonOctreeIndex.delete(obj);
    }

    const entry = this.octreeEntries.get(obj);
    if (entry && this.octree) {
      // After distribute(), entries live in leaf nodes — calling remove() on
      // the root would only update objectMap and never touch the leaves.
      this.octree.removeEverywhere(entry);
      this.octreeEntries.delete(obj);
      this.octreeRemovesSincePrune++;
      if (this.octreeRemovesSincePrune >= ObjectManager.PRUNE_INTERVAL) {
        this.octree.prune();
        this.octreeRemovesSincePrune = 0;
      }
    }

    this.removed.next(obj);
  }
}

export type ObjectInteraction = {
  onPressDown?: () => void;
  onPressRelease?: () => void;
  onHoverIn?: () => void;
  onHoverOut?: () => void;
};
